import React from "react";
import {
  Activity,
  AlertTriangle,
  Trophy,
  TrendingUp,
  Sparkles,
  CalendarClock,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProgressRing } from "../ProgressRing";
import { useInsights } from "../../data/insightsStore";

const Section = ({ title, icon: Icon, action, onAction, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white cf-shadow p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-500" />}
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{title}</p>
      </div>
      {action && (
        <button
          onClick={onAction}
          className="text-[11.5px] font-semibold text-indigo-700 hover:underline"
        >
          {action}
        </button>
      )}
    </div>
    {children}
  </div>
);

export const RiskIntelligencePanel = () => {
  const navigate = useNavigate();
  const { data } = useInsights();
  const feed = data?.intel_feed;
  if (!feed) {
    return (
      <aside
        className="hidden xl:flex fixed right-0 top-0 w-[360px] h-screen flex-col border-l cf-divider-soft z-30 cf-scroll overflow-y-auto"
        style={{ background: "var(--cf-app-bg)" }}
        data-testid="risk-intelligence-panel"
      >
        <div className="p-6 text-slate-500 text-sm">Loading intelligence feed…</div>
      </aside>
    );
  }
  const exch = feed.exchange_prob || { value: 0, trend: "0", subtitle: "—" };

  return (
    <aside
      className="hidden xl:flex fixed right-0 top-0 w-[360px] h-screen flex-col border-l cf-divider-soft z-30 cf-scroll overflow-y-auto" style={{ background: "var(--cf-app-bg)" }}
      data-testid="risk-intelligence-panel"
    >
      <div className="px-6 pt-6 pb-2 sticky top-0 z-10">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Live feed</p>
        <h3 className="font-display text-[20px] font-bold text-slate-900 mt-1">Risk command</h3>
      </div>

      <div className="px-6 pb-8 space-y-4">
        {/* Exchange probability */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 cf-shadow" data-testid="exchange-prob-card">
          <div className="flex items-center gap-2 text-indigo-300">
            <TrendingUp size={14} />
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">Exchange probability tracker</p>
          </div>
          <div className="mt-3 flex items-end gap-4">
            <ProgressRing value={exch.value} size={68} stroke={6} color="#6366F1" label={`${exch.value}%`} />
            <div>
              <p className="font-display text-[18px] font-bold leading-none">{exch.subtitle || "Portfolio-weighted"}</p>
              <p className="text-[12px] text-indigo-300 font-semibold mt-1">{exch.trend} vs target</p>
            </div>
          </div>
        </div>

        {/* Today's escalations */}
        <Section title="Today's risk escalations" icon={Activity} action="Live feed">
          {feed.today_escalations.length === 0 ? (
            <p className="text-[12px] text-slate-500">No escalations in the last 36h — pipeline calm.</p>
          ) : (
            <ul className="relative space-y-3">
              <span className="absolute left-[6px] top-1 bottom-1 w-px bg-slate-200" />
              {feed.today_escalations.map((e, i) => (
                <li key={i} data-testid={`escalation-${i}`} className="flex items-start gap-3">
                  <span className={`relative z-10 mt-1 w-3 h-3 rounded-full ${i === 0 ? "bg-indigo-500 cf-pulse" : "bg-white border-2 border-slate-300"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] text-slate-800 leading-tight">{e.text}</p>
                    <p className="text-[10.5px] text-slate-400 font-mono-cf mt-0.5">{e.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Most delayed */}
        <Section title="Most delayed transactions" icon={AlertTriangle}>
          {feed.most_delayed.length === 0 ? (
            <p className="text-[12px] text-slate-500">All transactions moved in the last 24h.</p>
          ) : (
            <ul className="space-y-2.5">
              {feed.most_delayed.map((d) => (
                <li
                  key={d.deal_id}
                  data-testid={`delayed-${d.deal_id}`}
                  onClick={() => navigate(`/deal-rooms/${d.deal_id}`)}
                  className="flex items-center justify-between cursor-pointer hover:bg-slate-50/80 rounded px-1 -mx-1 py-0.5"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-slate-900 truncate">{d.name}</p>
                    <p className="text-[11px] text-slate-500">{d.days} day{d.days === 1 ? "" : "s"} since last activity</p>
                  </div>
                  <span className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                    d.risk === "high" ? "bg-rose-50 text-rose-700 border-rose-200" :
                    d.risk === "med" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                       "bg-indigo-50 text-indigo-700 border-indigo-200"
                  }`}>{d.risk === "high" ? "High" : d.risk === "med" ? "Watch" : "Low"}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Solicitor ranks */}
        <Section title="Solicitor response rankings" icon={Trophy} action="View all">
          {feed.solicitor_ranks.length === 0 ? (
            <p className="text-[12px] text-slate-500">No solicitors active in the pipeline yet.</p>
          ) : (
            <ul className="space-y-2.5">
              {feed.solicitor_ranks.map((s, i) => (
                <li key={s.name} data-testid={`sol-rank-${i}`} className="flex items-center gap-3">
                  <span className="font-mono-cf text-[11px] text-slate-400 w-4">{i + 1}</span>
                  <p className="flex-1 text-[12.5px] font-medium text-slate-900 truncate">{s.name}</p>
                  <p className={`font-display text-[14px] font-bold ${s.score >= 90 ? "text-indigo-700" : s.score >= 80 ? "text-slate-900" : "text-amber-700"}`}>{s.score}</p>
                  <span className={`text-[10.5px] font-semibold w-7 text-right ${String(s.trend).startsWith("-") ? "text-rose-600" : s.trend === "0" || s.trend === "+0" ? "text-slate-400" : "text-indigo-600"}`}>{s.trend}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* AI interventions */}
        <Section title="AI intervention recommendations" icon={Sparkles} action="Apply all">
          {feed.interventions.length === 0 ? (
            <p className="text-[12px] text-slate-500">No interventions required — pipeline operating clean.</p>
          ) : (
            <ul className="space-y-3">
              {feed.interventions.map((iv) => (
                <li key={iv.id} data-testid={`intervention-${iv.id}`} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <p className="text-[12.5px] font-semibold text-slate-900 leading-snug">{iv.title}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-indigo-700">{iv.impact}</span>
                    <span className="text-[10.5px] text-slate-500 font-mono-cf">conf {iv.confidence}%</span>
                  </div>
                  <button className="mt-2 w-full h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11.5px] font-semibold transition-colors">
                    Apply
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Predicted completions */}
        <div className="rounded-2xl border border-indigo-200/80 bg-indigo-50/40 p-5" data-testid="predicted-completions-card">
          <div className="flex items-center gap-2 text-indigo-700">
            <CalendarClock size={14} />
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">Predicted completions · next 14 days</p>
          </div>
          <p className="font-display text-[28px] font-bold text-slate-900 mt-1.5 leading-none">{feed.predicted_completions} deal{feed.predicted_completions === 1 ? "" : "s"}</p>
          <p className="text-[12px] text-slate-600 mt-2">Modelled probability ≥ 60%, exchange target within 14 days.</p>
        </div>

        {/* Prevention opportunities */}
        <Section title="Fall-through prevention" icon={ShieldCheck}>
          <ul className="space-y-2.5">
            {(feed.prevention_opportunities || []).map((p, i) => (
              <li key={i} data-testid={`prevention-${i}`} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                <p className="text-[12.5px] text-slate-700 leading-relaxed">{p.title}</p>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </aside>
  );
};
